import { connect } from 'react-redux';
import * as React from 'react';
import difference from 'lodash.difference';
import intersection from 'lodash.intersection';
import { Table, Checkbox } from 'semantic-ui-react';
import { FieldInputProps } from 'react-final-form';

// @ts-ignore
import EntityTypeName from 'components/EntityTypeName';
import EntityTypeParentName from 'components/EntityTypeParentName';
// @ts-ignore
import ResponsiveTable from 'components/ResponsiveTable';

import {
    SprayEventEndDate,
    SprayEventId,
    SprayEventStartDate
} from 'services/sprayEvents/types';
import { EntityTypeId } from 'services/entityTypes/types';
import { EntityId } from 'services/entities/types';
import ErrorGate from 'components/ErrorGate';
import { updateQueryString as updateQueryStringAction } from 'services/router/actions';
import RenderSubmissionSprayEventsTableRows from './components/RenderSubmissionSprayEventsTableRows';
import SprayEventsPagination from './components/SprayEventsPagination';

import './styles.scss';

export interface OwnProps {
    input: FieldInputProps<any, HTMLElement>;
    entityId?: EntityId;
    startDate?: SprayEventStartDate;
    endDate?: SprayEventEndDate;
    spraySchemaEntityTypeId: EntityTypeId;
    spraySchemaInnerEntityTypeId: EntityTypeId;
    sprayEventIds: SprayEventId[];
    sprayEventIdsLength: number;
    paginationName: string;
}

interface DispatchProps {
    updateQueryString: typeof updateQueryStringAction;
}

type Props = OwnProps & DispatchProps;

export class RenderSubmissionSprayEventsTableComponent extends React.PureComponent<Props> {
    componentDidUpdate(prevProps: Props): void {
        const {
            input: { onChange },
            entityId,
            startDate,
            endDate,
            updateQueryString,
            paginationName
        } = this.props;

        if (
            entityId !== prevProps.entityId ||
            startDate !== prevProps.startDate ||
            endDate !== prevProps.endDate
        ) {
            onChange([]);
            updateQueryString({ [paginationName]: undefined });
        }
    }

    get checked(): boolean {
        const {
            input: { value },
            sprayEventIds
        } = this.props;
        if (!sprayEventIds.length) {
            return false;
        }
        return !difference(sprayEventIds, value).length;
    }

    get indeterminate(): boolean {
        const {
            input: { value },
            sprayEventIds
        } = this.props;

        if (this.checked) {
            return false;
        }

        return !!intersection(value, sprayEventIds).length;
    }

    handleChange = (): void => {
        const {
            input: { onChange, value },
            sprayEventIds
        } = this.props;

        if (!this.checked && !this.indeterminate && sprayEventIds) {
            onChange([...value, ...sprayEventIds]);
        } else {
            const values = value.filter((id: number) => {
                return !sprayEventIds.includes(id);
            });

            onChange(values);
        }
    };

    handleToggleTableRowCheckbox = (_event: any, { id }: any): void => {
        const {
            input: { onChange, ...input }
        } = this.props;
        const values = input.value || [];

        if (values.includes(id)) {
            const nextValues = values.filter((value: number) => value !== id);
            // remove id from values
            onChange(nextValues);
        } else {
            // Append id to values
            onChange([...values, id]);
        }
    };

    render(): React.ReactElement {
        const {
            input: { value },
            spraySchemaEntityTypeId,
            spraySchemaInnerEntityTypeId,
            sprayEventIds,
            entityId,
            startDate,
            endDate,
            sprayEventIdsLength,
            paginationName
        } = this.props;

        return (
            <ErrorGate namespace="SPRAY_EVENTS_FOR_SUBMISSION_LOAD">
                <ResponsiveTable>
                    <Table sortable striped singleLine unstackable size="small">
                        <Table.Header
                            style={{
                                fontSize: 14
                            }}
                        >
                            <Table.Row>
                                <Table.HeaderCell
                                    collapsing
                                    textAlign="center"
                                    verticalAlign="middle"
                                    className="events-table-cell-padding"
                                    style={{
                                        lineHeight: 0,
                                        borderRadius: 0
                                    }}
                                >
                                    <Checkbox
                                        checked={this.checked}
                                        indeterminate={this.indeterminate}
                                        onChange={this.handleChange}
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    className="events-table-cell-padding"
                                    collapsing
                                >
                                    Date
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    className="events-table-cell-padding"
                                    collapsing
                                >
                                    Application Type
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    className="events-table-cell-padding"
                                    collapsing
                                >
                                    <EntityTypeName
                                        entityTypeId={spraySchemaEntityTypeId}
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    className="events-table-cell-padding"
                                    collapsing
                                >
                                    Variety
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    className="events-table-cell-padding"
                                    collapsing
                                >
                                    Applicator
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    className="events-table-cell-padding"
                                    collapsing
                                >
                                    Final Spray
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    className="events-table-cell-padding"
                                    width={3}
                                >
                                    <EntityTypeParentName
                                        plural
                                        entityTypeId={
                                            spraySchemaInnerEntityTypeId
                                        }
                                    />
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    className="events-table-cell-padding"
                                    width={3}
                                >
                                    <EntityTypeName
                                        plural
                                        entityTypeId={
                                            spraySchemaInnerEntityTypeId
                                        }
                                    />
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            <RenderSubmissionSprayEventsTableRows
                                value={value}
                                sprayEventIds={sprayEventIds}
                                onToggleTableRowCheckbox={
                                    this.handleToggleTableRowCheckbox
                                }
                            />
                        </Table.Body>
                    </Table>
                </ResponsiveTable>
                <div className="spray-events-pagination">
                    <div>
                        <SprayEventsPagination
                            entityId={entityId}
                            startDate={startDate}
                            endDate={endDate}
                            paginationName={paginationName}
                            length={sprayEventIdsLength}
                        />
                    </div>
                </div>
            </ErrorGate>
        );
    }
}

export default connect(null, {
    updateQueryString: updateQueryStringAction
})(RenderSubmissionSprayEventsTableComponent);
